/**
 * Reading a paper document from a photo or scan (F03, F09; decision 507; docs/specs/document-reading.md): the kinds
 * of document, the values each holds, the files Teslyn takes, the reader's prompt and JSON schema per kind, and the
 * normalising of a reading (the model's answer, or what a person typed) into checked shapes. The checks against
 * Teslyn's records are `document-checks.ts`.
 *
 * This file has no imports on purpose: the Edge Function `document-read` (Deno) uses an identical copy,
 * `supabase/functions/_shared/document-reading.ts` (`node tools/dev/copy-edge-shared.mjs` writes it, and its
 * `--check` fails while the two differ). Edit this file, then run the tool.
 */

export type DocumentKind = 'mr_front' | 'mr_log' | 'fuel_docket' | 'fuel_receipt';
export const DOCUMENT_KINDS: readonly DocumentKind[] = ['mr_front', 'mr_log', 'fuel_docket', 'fuel_receipt'];

/** What the screens call each kind. */
export const DOCUMENT_KIND_LABEL: Readonly<Record<DocumentKind, string>> = {
  mr_front: 'MR front page',
  mr_log: 'MR daily log page',
  fuel_docket: 'Fuel delivery docket',
  fuel_receipt: 'Fuel receipt',
};

export function isDocumentKind(v: unknown): v is DocumentKind {
  return typeof v === 'string' && (DOCUMENT_KINDS as readonly string[]).includes(v);
}

/** The Edge Function that reads a picture (docs/specs/document-reading.md §2). */
export const DOCUMENT_READ_FUNCTION = 'document-read';
/** The integration provider whose key the function uses (the organisation's own, decision 6). */
export const DOCUMENT_READ_PROVIDER = 'anthropic';
/** The model the function asks. */
export const DOCUMENT_READ_MODEL = 'claude-opus-5-5';

/** The files a document may be, with the content type each is stored and read as. HEIC is refused (decision 516). */
export const DOCUMENT_FILE_TYPES: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  pdf: 'application/pdf',
};
/** The largest file kept (as a fuel docket, decision 193). */
export const DOCUMENT_MAX_BYTES = 30 * 1024 * 1024;
/** A photo is fitted to this long edge (px) before it is read. */
export const DOCUMENT_READ_MAX_PX = 2400;
/** The largest picture sent to the reader: a fitted photo, or a PDF as it is. */
export const DOCUMENT_READ_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const DOCUMENT_READ_PDF_MAX_BYTES = 10 * 1024 * 1024;
/** The media types the reader takes. */
export const DOCUMENT_READ_MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
] as const;
export type DocumentReadMediaType = (typeof DOCUMENT_READ_MEDIA_TYPES)[number];

/** Why a file cannot be a document, or null when it can. */
export function documentFileProblem(fileName: string, sizeBytes: number): string | null {
  const dot = fileName.lastIndexOf('.');
  const given = dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  // a bitmap is taken and kept as a PNG (R144; the rule is `image-import.ts`, repeated here only because this file
  // stays import-free for the Edge Function's copy)
  const ext = given === 'bmp' || given === 'dib' ? 'png' : given;
  if (ext === 'heic' || ext === 'heif')
    return 'HEIC photos cannot be read yet. On an iPhone choose Settings, Camera, Formats, Most Compatible, or send the photo as a JPEG.';
  if (!(ext in DOCUMENT_FILE_TYPES))
    return 'A document must be a photo (JPG, PNG, WebP or BMP) or a PDF scan.';
  if (!(sizeBytes > 0)) return 'The file is empty.';
  if (sizeBytes > DOCUMENT_MAX_BYTES)
    return `The file is ${(sizeBytes / 1024 / 1024).toFixed(1)} MB; the limit is ${DOCUMENT_MAX_BYTES / 1024 / 1024} MB.`;
  return null;
}

// ------------------------------------------------------------------------------------------------ values

export interface MrFrontValues {
  readonly mr_number: string | null;
  readonly registration: string | null;
  readonly aircraft_type: string | null;
  /** ISO date the MR expires, if not first by hours. */
  readonly expiry_date: string | null;
  /** The aircraft total time in service at which it expires. */
  readonly expiry_hours: number | null;
  /** Aircraft total time in service at issue. */
  readonly issue_hours: number | null;
  readonly issue_date: string | null;
  /** `HH:MM`. */
  readonly issue_time: string | null;
  readonly issue_place: string | null;
  readonly issued_by: string | null;
  readonly operational_category: string | null;
}

export interface MrLogRow {
  readonly date: string | null;
  readonly flight_hours: number | null;
  readonly progressive_hours: number | null;
}

export interface MrLogValues {
  readonly registration: string | null;
  readonly brought_forward_hours: number | null;
  readonly rows: readonly MrLogRow[];
}

export type FuelUnitOnDocket = 'L' | 'USG';
export type FuelTypeOnDocket = 'avgas' | 'turbine';

/** A delivery docket of a resupply, or the receipt of a refuel. */
export interface DocketValues {
  readonly date: string | null;
  readonly time: string | null;
  readonly litres: number | null;
  readonly unit: FuelUnitOnDocket | null;
  readonly fuel_type: FuelTypeOnDocket | null;
  readonly registration: string | null;
  readonly supplier: string | null;
  readonly docket_number: string | null;
  readonly location: string | null;
}

export interface DocumentValuesByKind {
  readonly mr_front: MrFrontValues;
  readonly mr_log: MrLogValues;
  readonly fuel_docket: DocketValues;
  readonly fuel_receipt: DocketValues;
}
export type DocumentValues = DocumentValuesByKind[DocumentKind];

const FRONT_KEYS = [
  'mr_number',
  'registration',
  'aircraft_type',
  'expiry_date',
  'expiry_hours',
  'issue_hours',
  'issue_date',
  'issue_time',
  'issue_place',
  'issued_by',
  'operational_category',
] as const;
const DOCKET_KEYS = [
  'date',
  'time',
  'litres',
  'unit',
  'fuel_type',
  'registration',
  'supplier',
  'docket_number',
  'location',
] as const;

/** The boxes of a kind, in the order the screens show them. */
export const DOCUMENT_FIELDS: Readonly<Record<DocumentKind, readonly string[]>> = {
  mr_front: FRONT_KEYS,
  mr_log: ['registration', 'brought_forward_hours', 'rows'],
  fuel_docket: DOCKET_KEYS,
  fuel_receipt: DOCKET_KEYS,
};

/** The words of each box. */
export const DOCUMENT_FIELD_LABEL: Readonly<Record<string, string>> = {
  mr_number: 'MR number',
  registration: 'Registration',
  aircraft_type: 'Aircraft type',
  expiry_date: 'Expires on',
  expiry_hours: 'Expires at (TTIS, h)',
  issue_hours: 'TTIS at issue (h)',
  issue_date: 'Issued on',
  issue_time: 'Issue time',
  issue_place: 'Issue place',
  issued_by: 'Issued by',
  operational_category: 'Operational category',
  brought_forward_hours: 'Brought forward (h)',
  rows: 'Daily rows',
  date: 'Date',
  time: 'Time',
  litres: 'Quantity',
  unit: 'Unit',
  fuel_type: 'Fuel type',
  supplier: 'Supplier',
  docket_number: 'Docket or receipt number',
  location: 'Location',
};

/** Which boxes hold numbers, dates and times (the rest are text or a choice). */
export const NUMBER_FIELDS: readonly string[] = [
  'expiry_hours',
  'issue_hours',
  'brought_forward_hours',
  'litres',
];
export const DATE_FIELDS: readonly string[] = ['expiry_date', 'issue_date', 'date'];
export const TIME_FIELDS: readonly string[] = ['issue_time', 'time'];

export function emptyValues<K extends DocumentKind>(kind: K): DocumentValuesByKind[K] {
  if (kind === 'mr_log')
    return {
      registration: null,
      brought_forward_hours: null,
      rows: [],
    } as unknown as DocumentValuesByKind[K];
  const keys = kind === 'mr_front' ? FRONT_KEYS : DOCKET_KEYS;
  return Object.fromEntries(keys.map((k) => [k, null])) as unknown as DocumentValuesByKind[K];
}

// ------------------------------------------------------------------------------------------------ parsing

const pad = (n: number): string => String(n).padStart(2, '0');

function validYmd(y: number, m: number, d: number): string | null {
  if (!(y >= 1900 && y <= 2199 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  const t = new Date(Date.UTC(y, m - 1, d));
  if (t.getUTCFullYear() !== y || t.getUTCMonth() !== m - 1 || t.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

/**
 * A date as an ISO string: `2027-09-25` as it is, or Australian day/month/year with `/`, `-`, `.` or spaces between
 * (`25/9/27`, `25-9-26`, `25 9 2027`); a two-digit year is 20yy. Null when it is not a real date.
 */
export function parseAuDate(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return validYmd(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})\s*[/.\-\s]\s*(\d{1,2})\s*[/.\-\s]\s*(\d{2}|\d{4})$/.exec(s);
  if (!m) return null;
  const y = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return validYmd(y, Number(m[2]), Number(m[1]));
}

/**
 * Hours as a number: `5290.3`, a handwritten `5290-3`, `5290·3` or `5290 3`, `10,1`; a number as it is. Null when it
 * is not a number of hours (negative, or over 100,000).
 */
export function parseHours(v: unknown): number | null {
  let n: number | null = null;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string') {
    const s = v.trim().replace(/\s*h(ours?|rs?)?$/i, '');
    const m = /^(\d{1,6})(?:\s*[.,·\-\s]\s*(\d{1,2}))?$/.exec(s);
    if (m) n = Number(`${m[1]}.${m[2] ?? '0'}`);
  }
  if (n === null || !Number.isFinite(n) || n < 0 || n > 100_000) return null;
  return Math.round(n * 100) / 100;
}

/** A quantity of fuel: `4,000`, `4000.5`, `4 000 L`. Null when it is not one (negative, or over 1,000,000). */
export function parseQuantity(v: unknown): number | null {
  let n: number | null = null;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string') {
    const s = v
      .trim()
      .replace(/\s*(l|litres?|liters?|usg|gal|us gal)\.?$/i, '')
      .replace(/[,\s](?=\d{3}\b)/g, '');
    if (/^\d+(\.\d+)?$/.test(s)) n = Number(s);
  }
  if (n === null || !Number.isFinite(n) || n < 0 || n > 1_000_000) return null;
  return Math.round(n * 100) / 100;
}

/** `HH:MM` from `10:00`, `1000`, `9.30`, `09:30`. */
export function parseDocumentTime(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const m = /^([01]?\d|2[0-3])\s*[:.h]?\s*([0-5]\d)$/i.exec(v.trim());
  return m ? `${pad(Number(m[1]))}:${m[2]}` : null;
}

/** An Australian or other registration as written: `JOZ` and `VH-JOZ` are `VH-JOZ`; `ZK-HEF` stays. */
export function parseRegistration(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toUpperCase().replace(/\s+/g, '');
  if (s === '') return null;
  if (/^[A-Z]{3}$/.test(s)) return `VH-${s}`;
  const m = /^(VH|ZK|P2|N|C|G|DQ)-?([A-Z0-9]{1,5})$/.exec(s);
  if (m) return `${m[1]}-${m[2]}`;
  return s.length <= 12 ? s : null;
}

const text = (v: unknown, max = 200): string | null => {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  const s = String(v).trim().replace(/\s+/g, ' ');
  return s === '' ? null : s.slice(0, max);
};

function fuelType(v: unknown): FuelTypeOnDocket | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (/^(avgas|100ll|100\/130|avgas ?100(ll)?)$/.test(s)) return 'avgas';
  if (/^(turbine|avtur|jet ?a-?1?|jet ?a1|jet fuel|kerosene)$/.test(s)) return 'turbine';
  return null;
}

function unitOf(v: unknown): FuelUnitOnDocket | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (/^(l|litres?|liters?|ltrs?)$/.test(s)) return 'L';
  if (/^(usg|us ?gal(lons?)?|gal(lons?)?)$/.test(s)) return 'USG';
  return null;
}

/** A reading or typed values as the kind's shape: anything not readable becomes null. */
export function normaliseValues<K extends DocumentKind>(kind: K, raw: unknown): DocumentValuesByKind[K] {
  const r =
    raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  if (kind === 'mr_front') {
    const v: MrFrontValues = {
      mr_number: text(r.mr_number, 40),
      registration: parseRegistration(r.registration),
      aircraft_type: text(r.aircraft_type, 60),
      expiry_date: parseAuDate(r.expiry_date),
      expiry_hours: parseHours(r.expiry_hours),
      issue_hours: parseHours(r.issue_hours),
      issue_date: parseAuDate(r.issue_date),
      issue_time: parseDocumentTime(r.issue_time),
      issue_place: text(r.issue_place, 60),
      issued_by: text(r.issued_by),
      operational_category: text(r.operational_category, 60),
    };
    return v as DocumentValuesByKind[K];
  }
  if (kind === 'mr_log') {
    const rows = Array.isArray(r.rows) ? r.rows : [];
    const v: MrLogValues = {
      registration: parseRegistration(r.registration),
      brought_forward_hours: parseHours(r.brought_forward_hours),
      rows: rows.slice(0, 60).map((x): MrLogRow => {
        const o = x !== null && typeof x === 'object' ? (x as Record<string, unknown>) : {};
        return {
          date: parseAuDate(o.date),
          flight_hours: parseHours(o.flight_hours),
          progressive_hours: parseHours(o.progressive_hours),
        };
      }),
    };
    return v as DocumentValuesByKind[K];
  }
  const v: DocketValues = {
    date: parseAuDate(r.date),
    time: parseDocumentTime(r.time),
    litres: parseQuantity(r.litres),
    unit: unitOf(r.unit) ?? (r.litres != null ? 'L' : null),
    fuel_type: fuelType(r.fuel_type),
    registration: parseRegistration(r.registration),
    supplier: text(r.supplier),
    docket_number: text(r.docket_number, 60),
    location: text(r.location),
  };
  return v as DocumentValuesByKind[K];
}

/** A reading as the function answers it: the values, and the boxes the reader was unsure of. */
export interface DocumentReading<K extends DocumentKind = DocumentKind> {
  readonly values: DocumentValuesByKind[K];
  /** Box names (`expiry_hours`, `rows.3.flight_hours`) to check closely. */
  readonly unsure: readonly string[];
}

/**
 * The model's answer as a reading: the values normalised, and as unsure every box the model named plus every box it
 * filled that did not parse (a reading that does not parse is shown empty, marked to check).
 */
export function normaliseReading<K extends DocumentKind>(kind: K, raw: unknown): DocumentReading<K> {
  const r =
    raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const values = normaliseValues(kind, r.values ?? r);
  const unsure = new Set<string>(
    Array.isArray(r.unsure)
      ? r.unsure.filter((x): x is string => typeof x === 'string' && x.length <= 60).slice(0, 100)
      : [],
  );
  const given = (r.values ?? r) as Record<string, unknown>;
  const lost = (src: unknown, got: unknown): boolean =>
    src !== null && src !== undefined && src !== '' && got === null;
  if (given && typeof given === 'object') {
    const v = values as unknown as Record<string, unknown>;
    for (const k of Object.keys(v)) if (k !== 'rows' && lost(given[k], v[k])) unsure.add(k);
    if (kind === 'mr_log' && Array.isArray(given.rows)) {
      const rows = (values as MrLogValues).rows;
      given.rows.slice(0, rows.length).forEach((g: unknown, i: number) => {
        const o = g !== null && typeof g === 'object' ? (g as Record<string, unknown>) : {};
        const row = rows[i]!;
        for (const k of ['date', 'flight_hours', 'progressive_hours'] as const)
          if (lost(o[k], row[k])) unsure.add(`rows.${i}.${k}`);
      });
    }
  }
  return { values, unsure: [...unsure].sort() };
}

/** True when nothing has been entered. */
export function valuesEmpty(kind: DocumentKind, v: DocumentValues): boolean {
  if (kind === 'mr_log') {
    const l = v as MrLogValues;
    return l.registration === null && l.brought_forward_hours === null && l.rows.length === 0;
  }
  return Object.values(v as unknown as Record<string, unknown>).every((x) => x === null);
}

// ------------------------------------------------------------------------------------------------ the reader

const nullable = (type: 'string' | 'number'): Record<string, unknown> => ({
  anyOf: [{ type }, { type: 'null' }],
});

function objectSchema(props: Record<string, Record<string, unknown>>): Record<string, unknown> {
  return { type: 'object', properties: props, required: Object.keys(props), additionalProperties: false };
}

/** The JSON schema the reader answers to, for one kind: `{ values: {…}, unsure: [box names] }`. */
export function documentReadSchema(kind: DocumentKind): Record<string, unknown> {
  let values: Record<string, unknown>;
  if (kind === 'mr_log') {
    values = objectSchema({
      registration: nullable('string'),
      brought_forward_hours: nullable('number'),
      rows: {
        type: 'array',
        items: objectSchema({
          date: nullable('string'),
          flight_hours: nullable('number'),
          progressive_hours: nullable('number'),
        }),
      },
    });
  } else {
    const keys = kind === 'mr_front' ? FRONT_KEYS : DOCKET_KEYS;
    values = objectSchema(
      Object.fromEntries(keys.map((k) => [k, nullable(NUMBER_FIELDS.includes(k) ? 'number' : 'string')])),
    );
  }
  return objectSchema({ values, unsure: { type: 'array', items: { type: 'string' } } });
}

const COMMON_RULES = [
  'Read only what is written on the page. Never guess or fill a box from what it usually says: leave it null.',
  'Dates are written day/month/year (Australia). Answer dates as YYYY-MM-DD; a two-digit year is 20yy.',
  'Answer times as HH:MM in 24 hours.',
  'List in "unsure" the name of every box whose writing you could not read with confidence (for daily rows, "rows.<index>.<box>", the first row being 0). A figure written over another is unsure.',
  'Do not copy signatures, licence numbers, names of people or card numbers.',
];

const KIND_RULES: Readonly<Record<DocumentKind, readonly string[]>> = {
  mr_front: [
    'The page is the front page (Part 1) of an Australian Civil Aviation Safety Authority Maintenance Release.',
    'mr_number is the printed serial number beside "Maintenance Release" (for example "A 353106").',
    'registration is the aircraft registration after "VH-" (answer it with VH-, for example "VH-JOZ").',
    'expiry_date and expiry_hours are the two figures in the EXPIRES box: the date, OR the aircraft time in service in hours.',
    'issue_hours is "Aircraft total time in service at issue"; issue_time, issue_date and issue_place are the boxes beside it.',
    'issued_by is the organisation the release was issued by or on behalf of, with its approval number if printed.',
    'Hours are decimal hours: a figure written "5290-3" or "5290·3" is 5290.3.',
  ],
  mr_log: [
    'The page is a Maintenance Release attachment with the daily inspection certification: one row per day with the date, a signature, a licence number, the flight time (hours and tenths) and the progressive total time in service (hours and tenths).',
    'brought_forward_hours is the "Brought Forward" progressive total at the top.',
    'For each written row give date, flight_hours (the flight time, for example 10.1 when written "10 · 1") and progressive_hours (for example 5206.4), in the order written. Leave out empty rows.',
    'registration is the aircraft registration printed or written on the page, with VH- for an Australian aircraft.',
  ],
  fuel_docket: [
    'The page is a fuel delivery docket: fuel delivered to a fuel truck or drums at a field base.',
    'litres is the quantity delivered; unit is "L" or "USG" as printed.',
    'fuel_type is "avgas" (AVGAS, 100LL) or "turbine" (Jet A-1, AVTUR).',
    'registration is an aircraft registration only when one is printed or written on the docket.',
  ],
  fuel_receipt: [
    'The page is a fuel receipt for fuel put into an aircraft (an airport bowser or a fuel agent).',
    'litres is the quantity uplifted into the aircraft; unit is "L" or "USG" as printed.',
    'fuel_type is "avgas" (AVGAS, 100LL) or "turbine" (Jet A-1, AVTUR).',
    'registration is the aircraft registration printed or written on the receipt.',
  ],
};

/** The reader's instructions for one kind (the system prompt). */
export function documentReadPrompt(kind: DocumentKind): string {
  return [
    `You read one ${DOCUMENT_KIND_LABEL[kind].toLowerCase()} from a photo or scan for an airborne survey company, so a person can check the values beside the picture.`,
    ...KIND_RULES[kind],
    ...COMMON_RULES,
    'Answer only with the JSON object of the schema.',
  ].join('\n');
}

/** The request the function makes of the reader, provider-neutral (the provider's adapter builds its own call). */
export interface DocumentReadRequest {
  readonly kind: DocumentKind;
  readonly model: string;
  readonly system: string;
  readonly schema: Record<string, unknown>;
  readonly mediaType: DocumentReadMediaType;
  /** Base64, no line breaks. */
  readonly data: string;
}

export function documentReadRequest(
  kind: DocumentKind,
  mediaType: DocumentReadMediaType,
  data: string,
  model: string = DOCUMENT_READ_MODEL,
): DocumentReadRequest {
  return {
    kind,
    model,
    system: documentReadPrompt(kind),
    schema: documentReadSchema(kind),
    mediaType,
    data,
  };
}

/** Why a request to read cannot be sent, or null: the kind, the media type and the size of the decoded picture. */
export function documentReadProblem(kind: unknown, mediaType: unknown, base64Length: number): string | null {
  if (!isDocumentKind(kind)) return 'Say which document this is.';
  if (typeof mediaType !== 'string' || !(DOCUMENT_READ_MEDIA_TYPES as readonly string[]).includes(mediaType))
    return 'A document is read from a JPEG, PNG or WebP photo or a PDF.';
  const bytes = Math.floor((base64Length * 3) / 4);
  if (bytes === 0) return 'The picture is empty.';
  const max = mediaType === 'application/pdf' ? DOCUMENT_READ_PDF_MAX_BYTES : DOCUMENT_READ_IMAGE_MAX_BYTES;
  if (bytes > max)
    return `The picture is ${(bytes / 1024 / 1024).toFixed(1)} MB; the reader takes ${max / 1024 / 1024} MB.`;
  return null;
}

// ------------------------------------------------------------------------------------------------ the boxes

/** A log page row as typed in its boxes. */
export interface MrLogRowText {
  readonly date: string;
  readonly flight_hours: string;
  readonly progressive_hours: string;
}

/** The values as the screens' boxes hold them: text per box, the log page's rows as text. */
export interface DocumentText {
  readonly fields: Readonly<Record<string, string>>;
  readonly rows: readonly MrLogRowText[];
}

const asText = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** Saved or read values as the boxes show them. */
export function valuesAsText(kind: DocumentKind, values: unknown): DocumentText {
  const v = normaliseValues(kind, values) as unknown as Record<string, unknown>;
  const fields: Record<string, string> = {};
  for (const k of DOCUMENT_FIELDS[kind]) if (k !== 'rows') fields[k] = asText(v[k]);
  const rows = Array.isArray(v.rows)
    ? (v.rows as MrLogRow[]).map((r) => ({
        date: asText(r.date),
        flight_hours: asText(r.flight_hours),
        progressive_hours: asText(r.progressive_hours),
      }))
    : [];
  return { fields, rows };
}

/** The boxes as values of the kind (what is saved and checked); a row with nothing typed is left out. */
export function valuesFromText(kind: DocumentKind, t: DocumentText): DocumentValues {
  const raw: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(t.fields)) raw[k] = v.trim() === '' ? null : v;
  if (kind === 'mr_log') raw.rows = t.rows.filter((r) => r.date || r.flight_hours || r.progressive_hours);
  return normaliseValues(kind, raw);
}
